import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import NumTextBox from './NumTextBox';

import {toFixedFloor} from '../../lib/numericHelper';

import './NumericInput.scss';

const NumericInput = React.forwardRef(
  ({value, onChange, precision = 1, min, max, increment = 0.1, changeOnBlur = false}, ref) => {
    const [valueState, setValueState] = useState(value);

    useEffect(() => {
      setValueState(value);
    }, [value]);

    function handleIncrement() {
      let val = parseFloat(value);

      if (Number.isNaN(val)) {
        val = 0;
      }

      val += parseFloat(increment);

      const roundedMax = toFixedFloor(max, precision);

      if (min > val) val = min;
      if (roundedMax < val) val = roundedMax;

      onChange(val === 0 ? 0 : parseFloat(val).toFixed(precision));
    }

    function handleDecrement() {
      let val = parseFloat(value);

      if (Number.isNaN(val)) {
        val = 0;
      }

      val -= parseFloat(increment);

      const roundedMax = toFixedFloor(max, precision);

      if (min > val) val = min;
      if (roundedMax < val) val = roundedMax;

      onChange(val === 0 ? 0 : parseFloat(val).toFixed(precision));
    }

    function handleOnBlur() {
      const val = changeOnBlur ? valueState : value;
      let newVal = val;

      if (value === valueState && changeOnBlur) return;

      if (val) {
        if (val.toString().startsWith('0.')) {
          newVal = val > 0 && val > min ? Number(parseFloat(val).toFixed(precision)) : min;
        } else {
          newVal = val > min ? Number(parseFloat(val).toFixed(precision)) : min;
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (min < 0 && max > 0) {
          newVal = 0;
        } else {
          newVal = min;
        }
      }

      onChange(newVal, true);
      setValueState(newVal);
    }

    const onChangeHandler = (e) => {
      if (changeOnBlur) {
        setValueState(e);
        return;
      }

      const val =
        e.toString().startsWith('-0') || e.toString() === '0.' || e.toString() === ''
          ? e
          : Number(parseFloat(e).toFixed(precision));
      const roundedMax = toFixedFloor(max, precision);

      if (val && val > roundedMax) {
        onChange(roundedMax);
        setValueState(roundedMax);

        return;
      }

      onChange(val);
      setValueState(e);
    };

    return (
      <div className="numeric-input" onBlur={handleOnBlur}>
        <NumTextBox
          ref={ref}
          value={valueState}
          onChange={onChangeHandler}
          precision={precision}
          min={changeOnBlur ? -9999 : min}
          max={parseFloat(max).toFixed(precision)}
          placeholder=""
        />
        <div className="numeric-input-arrow">
          <span className="glyphicon glyphicon-triangle-top" role="presentation" onClick={handleIncrement} />
          <span className="glyphicon glyphicon-triangle-bottom" role="presentation" onClick={handleDecrement} />
        </div>
      </div>
    );
  },
);

NumericInput.propTypes = {
  value: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  precision: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  increment: PropTypes.number,
  changeOnBlur: PropTypes.bool,
};

NumericInput.defaultProps = {
  precision: 1,
  min: -9999,
  max: 9999,
  increment: 0.1,
  changeOnBlur: false,
};

export default NumericInput;
